import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { locations as locApi } from '@/api/endpoints';
import type { LocationType } from '@/types/api';
import { TYPES } from '@/pages/LocationsPage';
import { getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the newly created location right before the dialog closes. */
  onCreated: (location: { id: number }) => void;
}

/**
 * Lightweight "create a location without leaving the page" dialog, used by the
 * zero-locations empty states (item form, dashboard, items list). Always
 * creates a root location — no parent picker — since the full tree/parent UI
 * already lives on the Locations page and this is meant to unblock, not
 * replace it. Its type options come from the same shared TYPES list as the
 * generic Locations form, which no longer includes HOME — creating a HOME is
 * exclusively done via NewHomeDialog's dedicated "+ Yeni Ev" flow (issue
 * #104), so this defaults to ROOM instead.
 */
export function LocationQuickCreateDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState<LocationType>('ROOM');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setType('ROOM');
    setError(null);
  };

  const create = useMutation({
    mutationFn: () => locApi.create({ name: name.trim(), type, description: null, parentId: null }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['locations'] });
      // Dashboard's empty-state CTA reads totalLocations off the ['stats'] query,
      // which has its own staleTime — without this, a user who quick-creates a
      // location and navigates back to the Dashboard within that window would
      // still see the "you have no locations" CTA for a location that now exists.
      void qc.invalidateQueries({ queryKey: ['stats'] });
      onCreated({ id: created.id });
      onOpenChange(false);
      reset();
    },
    onError: (err) => {
      setError(getApiErrorMessage(err, t('errors.generic')));
    },
  });

  // This only fires for user-driven close interactions (Cancel, Escape) — the
  // dialog has no Trigger of its own, and `open` is otherwise fully controlled
  // by the parent — so it's safe to always reset here, keeping the form from
  // showing stale input the next time it's opened.
  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    reset();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || create.isPending) return;
    setError(null);
    create.mutate();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('locations.quickCreateTitle')}</AlertDialogTitle>
        </AlertDialogHeader>
        <form onSubmit={onSubmit} className="space-y-3" aria-label="location-quick-create-form">
          <div className="space-y-1">
            <Label htmlFor="quick-loc-name">{t('locations.name')}</Label>
            <Input
              id="quick-loc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="quick-loc-type">{t('locations.type')}</Label>
            <select
              id="quick-loc-type"
              value={type}
              onChange={(e) => setType(e.target.value as LocationType)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {TYPES.map((tt) => (
                <option key={tt} value={tt}>{t(`locations.types.${tt}`)}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">{t('locations.quickCreateHint')}</p>
          {error && <div role="alert" className="text-sm text-destructive">{error}</div>}
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </AlertDialogCancel>
            <Button type="submit" loading={create.isPending} disabled={!name.trim() || create.isPending}>
              {t('common.create')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
